import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';

import {
    loadContractAssignmentByContractId,
    loadContractAssignmentByContractIdFailure,
    loadContractAssignmentByContractIdSuccessfully,
    updateCandidateCredentialingSpecialistFailure,
    updateCandidateCredentialingSpecialistSuccessfully,
    updateCandidateCredentialingSpecialistFromTeamInfo,
    updateContractCredentialingSpecialistFailure,
    updateContractCredentialingSpecialistSuccessfully,
    updateCredentialingSpecialistFromPrestartGrid,
    updateContractCredentialingSpecialist,
    loadContractAssignmentByContractIdFromCSEdit,
    loadFacilityAssignmentByFacilityId,
    loadFacilityAssignmentByFacilityIdSuccessfully,
    loadFacilityAssignmentByFacilityIdFailure,
    updateFacilityAssignmentByFacilityId,
    updateFacilityAssignmentByFacilityIdFailure,
    updateFacilityAssignmentByFacilityIdSuccessfully,
    bulkUpdateCredentialingSpecialist,
    bulkUpdateCredentialingSpecialistSuccess,
    bulkUpdateCredentialingSpecialistFailure,
    bulkUnassignCredentialingSpecialist,
    bulkUnassignCredentialingSpecialistSuccess,
    bulkUnassignCredentialingSpecialistFailure,
    bulkUpdateCredentialingSpecialistFromFacilityGrid,
    bulkUpdateCredentialingSpecialistFromFacilityGridSuccess,
    bulkUpdateCredentialingSpecialistFromFacilityGridFailure,
    bulkUnassignCredentialingSpecialistFromFacilityGridSuccess,
    bulkUnassignCredentialingSpecialistFromFacilityGridFailure,
    bulkUnassignCredentialingSpecialistFromFacilityGrid,
    loadLiaisonFacilityAssignmentByFacilityId,
    updateLiaisonFacilityAssignmentByFacilityId,
    updateLiaisonFacilityAssignmentByFacilityIdSuccessfully,
    loadLiaisonFacilityAssignmentByFacilityIdSuccessfully,
    loadLiaisonFacilityAssignmentByFacilityIdFailure,
    updateLiaisonFacilityAssignmentByFacilityIdFailure,
    loadLiaisonQmAssociationAssignmentByAssociationId,
    loadLiaisonQmAssociationAssignmentByAssociationIdSuccessfully,
    loadLiaisonQmAssociationAssignmentByAssociationIdFailure,
    updateLiaisonQmAssociationAssignmentByAssociationId,
    updateLiaisonQmAssociationAssignmentByAssociationIdFailure,
    updateLiaisonQmAssociationAssignmentByAssociationIdSuccessfully,
    bulkUpdateComplianceLiaisonFromFacilityGrid,
    bulkUpdateComplianceLiaisonFromFacilityGridSuccess,
    bulkUpdateComplianceLiaisonFromFacilityGridFailure,
    bulkUnassignComplianceLiaisonFromFacilityGrid,
    bulkUnassignComplianceLiaisonFromFacilityGridSuccess,
    bulkUnassignComplianceLiaisonFromFacilityGridFailure,
    updateContractComplianceLiaison,
    updateContractComplianceLiaisonSuccessfully,
    unassignComplianceLiaisonFromFacilityProfile,
    unassignComplianceLiaisonFromFacilityProfileSuccess,
    unassignComplianceLiaisonFromFacilityProfileFailure,
    unassignComplianceLiaisonFromAssociationProfile,
    unassignComplianceLiaisonFromAssociationProfileSuccess,
    unassignComplianceLiaisonFromAssociationProfileFailure,
    updateContractComplianceLiaisonFailure,
    unassignCredentialingSpecialistFromFacilityProfile,
    unassignCredentialingSpecialistFromFacilityProfileSuccess,
    unassignCredentialingSpecialistFromFacilityProfileFailure,
    unassignComplianceLiaisonFromCLEditReport,
    unassignComplianceLiaisonFromCLEditReportSuccessfully,
    unassignComplianceLiaisonFromCLEditReportFailure,
    unassignCredentialingSpecialistFromCSEditReport,
    unassignCredentialingSpecialistFromCSEditReportSuccess,
    unassignCredentialingSpecialistFromCSEditReportFailure,
    bulkUnassignCredentialingSpecialistFromRecruitingCandidatesGrid,
    bulkUnassignCredentialingSpecialistFromRecruitingCandidatesGridSuccess,
    bulkUnassignCredentialingSpecialistFromRecruitingCandidatesGridFailure,
    bulkUpdateCredentialingSpecialistFromRecruitingCandidatesGrid,
    bulkUpdateCredentialingSpecialistFromRecruitingCandidatesGridSuccess,
    bulkUpdateCredentialingSpecialistFromRecruitingCandidatesGridFailure
} from 'src/app/compliance/store/actions/compliance.actions';
import {
    getFacilities,
    updateFacilityBulkAutoMatchStatusSuccess,
    updateFacilityBulkCompleteStatusSuccess
} from 'src/app/compliance/store/actions/facilities.actions';
import { loadTeamInfo } from 'src/app/recruiting/store/recruiting.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';

import { ContractAssignmentService } from 'src/app/shared/services/contract-assignments.service';
import { AssigneeRoleType } from '../models/enums/assignee-role-type.enum';
import { bulkQmAssociationAssignmentChangeSuccess } from 'src/app/compliance/store/actions/qm-associations.actions';

@Injectable()
export class ContractAssignmentsEffects {
    getContractAssignmentByContractId$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadContractAssignmentByContractId, loadContractAssignmentByContractIdFromCSEdit),
            switchMap((action) => {
                if (!action.contractId) {
                    return of(loadContractAssignmentByContractIdFailure({ error: 'Contract ID is required' }));
                }
                return this._contractAssignmentService.getContractAssignmentByContractId(action.contractId).pipe(
                    map((result) => loadContractAssignmentByContractIdSuccessfully({ assignment: result })),
                    catchError((error: unknown) => {
                        console.error('loadContractAssignmentByContractId error:', error);
                        return of(loadContractAssignmentByContractIdFailure({ error }));
                    })
                );
            })
        )
    );

    getFacilityAssignmentByFacilityId$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadFacilityAssignmentByFacilityId, updateFacilityAssignmentByFacilityIdSuccessfully),
            switchMap((action) => {
                return this._contractAssignmentService
                    .getFacilityAssignmentByFacilityId(action.facilityId, action.assigneeRole)
                    .pipe(
                        map((result) => loadFacilityAssignmentByFacilityIdSuccessfully({ assignment: result })),
                        catchError((error: unknown) => of(loadFacilityAssignmentByFacilityIdFailure({ error })))
                    );
            })
        )
    );

    getLiaisonFacilityAssignmentByFacilityId$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadLiaisonFacilityAssignmentByFacilityId, updateLiaisonFacilityAssignmentByFacilityIdSuccessfully),
            switchMap((action) => {
                return this._contractAssignmentService
                    .getFacilityAssignmentByFacilityId(action.facilityId, action.assigneeRole)
                    .pipe(
                        map((result) => loadLiaisonFacilityAssignmentByFacilityIdSuccessfully({ assignment: result })),
                        catchError((error: unknown) => of(loadLiaisonFacilityAssignmentByFacilityIdFailure({ error })))
                    );
            })
        )
    );

    getLiaisonQmAssociationAssignmentByAssocId$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                loadLiaisonQmAssociationAssignmentByAssociationId,
                updateLiaisonQmAssociationAssignmentByAssociationIdSuccessfully
            ),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .getQmAssignmentByAssocId(action.associationId, action.assigneeRole)
                    .pipe(
                        map((result) =>
                            loadLiaisonQmAssociationAssignmentByAssociationIdSuccessfully({ assignment: result })
                        ),
                        catchError((error) => of(loadLiaisonQmAssociationAssignmentByAssociationIdFailure({ error })))
                    );
            })
        )
    );

    updateFacilityAssignmentConfiguration$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateFacilityAssignmentByFacilityId),
            switchMap((action) => {
                return this._contractAssignmentService
                    .updateFacilityAssignmentConfiguration(action.facilityId, action.assignment)
                    .pipe(
                        map(() =>
                            updateFacilityAssignmentByFacilityIdSuccessfully({
                                facilityId: action.facilityId,
                                assigneeRole: action.assignment.assigneeRole
                            })
                        ),
                        catchError((error: unknown) => of(updateFacilityAssignmentByFacilityIdFailure({ error })))
                    );
            })
        )
    );

    updateLiaisonFacilityAssignmentConfiguration$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateLiaisonFacilityAssignmentByFacilityId),
            switchMap((action) => {
                return this._contractAssignmentService
                    .updateFacilityAssignmentConfiguration(action.facilityId, action.assignment)
                    .pipe(
                        map(() =>
                            updateLiaisonFacilityAssignmentByFacilityIdSuccessfully({
                                facilityId: action.facilityId,
                                assigneeRole: action.assignment.assigneeRole
                            })
                        ),
                        catchError((error: unknown) =>
                            of(updateLiaisonFacilityAssignmentByFacilityIdFailure({ error }))
                        )
                    );
            })
        )
    );

    updateLiaisonQmAssociationConfiguration$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateLiaisonQmAssociationAssignmentByAssociationId),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateQmAssociationAssignmentConfiguration(action.associationId, action.assignment)
                    .pipe(
                        map(() =>
                            updateLiaisonQmAssociationAssignmentByAssociationIdSuccessfully({
                                associationId: action.associationId,
                                assigneeRole: action.assignment.assigneeRole
                            })
                        ),
                        catchError((error) => of(updateLiaisonQmAssociationAssignmentByAssociationIdFailure({ error })))
                    );
            })
        )
    );

    bulkUpdateCredentialingSpecialist$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUpdateCredentialingSpecialist),
            exhaustMap((action) => {
                return this._contractAssignmentService.updateBulkAssignmentConfiguration(action.bulkAssignment).pipe(
                    map(() => bulkUpdateCredentialingSpecialistSuccess()),
                    catchError((error: unknown) => of(bulkUpdateCredentialingSpecialistFailure({ error })))
                );
            })
        )
    );

    bulkUnassignCredentialingSpecialist$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUnassignCredentialingSpecialist),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() => bulkUnassignCredentialingSpecialistSuccess()),
                        catchError((error: unknown) => of(bulkUnassignCredentialingSpecialistFailure({ error })))
                    );
            })
        )
    );

    unassignComplianceLiaisonFromCLEditReport$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignComplianceLiaisonFromCLEditReport),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() =>
                            unassignComplianceLiaisonFromCLEditReportSuccessfully({
                                id: action.bulkUnassignAssignment.assignmentLevelIds[0]
                            })
                        ),
                        catchError((error: unknown) => of(unassignComplianceLiaisonFromCLEditReportFailure({ error })))
                    );
            })
        )
    );

    unassignCredentialingSpecialistFromCSEditReport$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignCredentialingSpecialistFromCSEditReport),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() =>
                            unassignCredentialingSpecialistFromCSEditReportSuccess({
                                id: action.bulkUnassignAssignment.assignmentLevelIds[0]
                            })
                        ),
                        catchError((error: unknown) =>
                            of(unassignCredentialingSpecialistFromCSEditReportFailure({ error }))
                        )
                    );
            })
        )
    );

    updateContractComplianceLiaisonAssignmentConfiguration$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateContractComplianceLiaison),
            switchMap((action) => {
                return this._contractAssignmentService
                    .updateContractAssignmentConfiguration(action.contractId, action.assignmentChange)
                    .pipe(
                        map((res) => updateContractComplianceLiaisonSuccessfully({ res })),
                        catchError((error: unknown) => of(updateContractComplianceLiaisonFailure({ error })))
                    );
            })
        )
    );

    bulkUpdateCredentialingSpecialistFromFacilityGrid$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUpdateCredentialingSpecialistFromFacilityGrid),
            exhaustMap((action) => {
                return this._contractAssignmentService.updateBulkAssignmentConfiguration(action.bulkAssignment).pipe(
                    map(() => bulkUpdateCredentialingSpecialistFromFacilityGridSuccess()),
                    catchError((error: unknown) =>
                        of(bulkUpdateCredentialingSpecialistFromFacilityGridFailure({ error }))
                    )
                );
            })
        )
    );

    bulkUnassignCredentialingSpecialistFromFacilityGrid$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUnassignCredentialingSpecialistFromFacilityGrid),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() => bulkUnassignCredentialingSpecialistFromFacilityGridSuccess()),
                        catchError((error: unknown) =>
                            of(bulkUnassignCredentialingSpecialistFromFacilityGridFailure({ error }))
                        )
                    );
            })
        )
    );

    bulkUpdateComplianceLiaisonFromFacilityGrid$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUpdateComplianceLiaisonFromFacilityGrid),
            exhaustMap((action) => {
                return this._contractAssignmentService.updateBulkAssignmentConfiguration(action.bulkAssignment).pipe(
                    map(() => bulkUpdateComplianceLiaisonFromFacilityGridSuccess()),
                    catchError((error: unknown) => of(bulkUpdateComplianceLiaisonFromFacilityGridFailure({ error })))
                );
            })
        )
    );

    bulkUnassignCredentialingSpecialistFromRecruitingCandidatesGrid$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUnassignCredentialingSpecialistFromRecruitingCandidatesGrid),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() => bulkUnassignCredentialingSpecialistFromRecruitingCandidatesGridSuccess()),
                        catchError((error: unknown) =>
                            of(bulkUnassignCredentialingSpecialistFromRecruitingCandidatesGridFailure({ error }))
                        )
                    );
            })
        )
    );

    bulkUpdateCredentialingSpecialistFromRecruitingCandidatesGrid$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUpdateCredentialingSpecialistFromRecruitingCandidatesGrid),
            exhaustMap((action) => {
                return this._contractAssignmentService.updateBulkAssignmentConfiguration(action.bulkAssignment).pipe(
                    map(() => bulkUpdateCredentialingSpecialistFromRecruitingCandidatesGridSuccess()),
                    catchError((error: unknown) =>
                        of(bulkUpdateCredentialingSpecialistFromRecruitingCandidatesGridFailure({ error }))
                    )
                );
            })
        )
    );

    bulkUnassignComplianceLiaisonFromFacilityGrid$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUnassignComplianceLiaisonFromFacilityGrid),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() => bulkUnassignComplianceLiaisonFromFacilityGridSuccess()),
                        catchError((error: unknown) =>
                            of(bulkUnassignComplianceLiaisonFromFacilityGridFailure({ error }))
                        )
                    );
            })
        )
    );

    unassignComplianceLiaisonFromAssociationProfile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignComplianceLiaisonFromAssociationProfile),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() =>
                            unassignComplianceLiaisonFromAssociationProfileSuccess({
                                id: action.bulkUnassignAssignment.assignmentLevelIds[0]
                            })
                        ),
                        catchError((error: unknown) =>
                            of(unassignComplianceLiaisonFromAssociationProfileFailure({ error }))
                        )
                    );
            })
        )
    );

    reloadAssociationProfile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignComplianceLiaisonFromAssociationProfileSuccess),
            map((action) =>
                loadLiaisonQmAssociationAssignmentByAssociationId({
                    associationId: action.id,
                    assigneeRole: AssigneeRoleType.ComplianceLiaison
                })
            )
        )
    );

    unassignComplianceLiaisonFromFacilityProfile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignComplianceLiaisonFromFacilityProfile),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() =>
                            unassignComplianceLiaisonFromFacilityProfileSuccess({
                                id: action.bulkUnassignAssignment.assignmentLevelIds[0]
                            })
                        ),
                        catchError((error: unknown) =>
                            of(unassignComplianceLiaisonFromFacilityProfileFailure({ error }))
                        )
                    );
            })
        )
    );

    unassignCredentialingSpecialistFromFacilityProfile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignCredentialingSpecialistFromFacilityProfile),
            exhaustMap((action) => {
                return this._contractAssignmentService
                    .updateExpireAssignmentConfiguration(action.bulkUnassignAssignment)
                    .pipe(
                        map(() =>
                            unassignCredentialingSpecialistFromFacilityProfileSuccess({
                                id: action.bulkUnassignAssignment.assignmentLevelIds[0]
                            })
                        ),
                        catchError((error: unknown) =>
                            of(unassignCredentialingSpecialistFromFacilityProfileFailure({ error }))
                        )
                    );
            })
        )
    );

    reloadCLFacilityProfile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignComplianceLiaisonFromFacilityProfileSuccess),
            map((action) =>
                loadLiaisonFacilityAssignmentByFacilityId({
                    facilityId: action.id,
                    assigneeRole: AssigneeRoleType.ComplianceLiaison
                })
            )
        )
    );

    reloadCSFacilityProfile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignCredentialingSpecialistFromFacilityProfileSuccess),
            map((action) =>
                loadFacilityAssignmentByFacilityId({
                    facilityId: action.id,
                    assigneeRole: AssigneeRoleType.CredentialingSpecialist
                })
            )
        )
    );

    showUnassignAssignedCSSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignCredentialingSpecialistFromCSEditReportSuccess),
            map(() => showToasterMessage({ message: 'Successfully unassigned Credentialing Specialist.' }))
        )
    );

    showUnassignAssignedCSFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignCredentialingSpecialistFromCSEditReportFailure),
            map(() => showToasterMessage({ message: 'Failed to unassign assigned Credentialing Specialist.' }))
        )
    );

    showUnassignAssignSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                bulkUnassignCredentialingSpecialistSuccess,
                bulkUnassignCredentialingSpecialistFromRecruitingCandidatesGridSuccess
            ),
            map(() => showToasterMessage({ message: 'Successfully bulk unassigned Credentialing Specialist.' }))
        )
    );

    showUnassignAssignFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                bulkUnassignCredentialingSpecialistFailure,
                bulkUnassignCredentialingSpecialistFromRecruitingCandidatesGridFailure
            ),
            map(() => showToasterMessage({ message: 'Failed to unassign assigned Credentialing Specialist.' }))
        )
    );

    showBulkAssignSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                bulkUpdateCredentialingSpecialistSuccess,
                bulkUpdateCredentialingSpecialistFromFacilityGridSuccess,
                bulkUpdateCredentialingSpecialistFromRecruitingCandidatesGridSuccess
            ),
            map(() => showToasterMessage({ message: 'Successfully bulk assigned Credentialing Specialist.' }))
        )
    );

    showBulkAssignFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                bulkUpdateCredentialingSpecialistFailure,
                bulkUpdateCredentialingSpecialistFromFacilityGridFailure,
                bulkUpdateCredentialingSpecialistFromRecruitingCandidatesGridFailure
            ),
            map(() => showToasterMessage({ message: 'Failed to bulk assign Credentialing Specialist.' }))
        )
    );

    showCLUnassignAssignSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUnassignComplianceLiaisonFromFacilityGridSuccess),
            map(() => showToasterMessage({ message: 'Successfully bulk unassigned Compliance Liaison.' }))
        )
    );

    showSingleCLUnassignAssignSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                unassignComplianceLiaisonFromFacilityProfileSuccess,
                unassignComplianceLiaisonFromAssociationProfileSuccess,
                updateContractComplianceLiaisonSuccessfully,
                unassignComplianceLiaisonFromCLEditReportSuccessfully
            ),
            map(() => showToasterMessage({ message: 'Successfully unassigned Compliance Liaison.' }))
        )
    );

    showSingleCSUnassignAssignSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(unassignCredentialingSpecialistFromFacilityProfileSuccess),
            map(() => showToasterMessage({ message: 'Successfully unassigned Credentialing Specialist.' }))
        )
    );

    showCLUnassignAssignFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                bulkUnassignComplianceLiaisonFromFacilityGridFailure,
                unassignComplianceLiaisonFromFacilityProfileFailure,
                unassignComplianceLiaisonFromAssociationProfileFailure,
                unassignComplianceLiaisonFromCLEditReportFailure
            ),
            map(() => showToasterMessage({ message: 'Failed to unassign assigned Compliance Liaison.' }))
        )
    );

    showCLBulkAssignSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUpdateComplianceLiaisonFromFacilityGridSuccess),
            map(() => showToasterMessage({ message: 'Successfully bulk assigned Compliance Liaison.' }))
        )
    );

    showCLBulkAssignFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(bulkUpdateComplianceLiaisonFromFacilityGridFailure),
            map(() => showToasterMessage({ message: 'Failed to bulk assign Compliance Liaison.' }))
        )
    );

    updateContractAssignmentConfiguration$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateContractCredentialingSpecialist, updateCredentialingSpecialistFromPrestartGrid),
            switchMap((action) => {
                return this._contractAssignmentService
                    .updateContractAssignmentConfiguration(action.contractId, action.assignmentChange)
                    .pipe(
                        map((res) => updateContractCredentialingSpecialistSuccessfully({ res })),
                        catchError((error: unknown) => of(updateContractCredentialingSpecialistFailure({ error })))
                    );
            })
        )
    );

    updateCandidateAssignmentConfiguration$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateCandidateCredentialingSpecialistFromTeamInfo),
            switchMap((action) => {
                return this._contractAssignmentService
                    .updateCandidateAssignmentConfiguration(action.candidateId, action.assignmentChange)
                    .pipe(
                        map(() =>
                            updateCandidateCredentialingSpecialistSuccessfully({ candidateId: action.candidateId })
                        ),
                        catchError((error: unknown) => of(updateCandidateCredentialingSpecialistFailure({ error })))
                    );
            })
        )
    );

    reloadTeamInfo$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateCandidateCredentialingSpecialistSuccessfully),
            map((action) => loadTeamInfo({ candidateId: action.candidateId }))
        )
    );

    reloadFacilities$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                bulkUpdateCredentialingSpecialistFromFacilityGridSuccess,
                bulkUnassignCredentialingSpecialistFromFacilityGridSuccess,
                bulkUnassignComplianceLiaisonFromFacilityGridSuccess,
                bulkUpdateComplianceLiaisonFromFacilityGridSuccess,
                bulkQmAssociationAssignmentChangeSuccess,
                updateFacilityBulkCompleteStatusSuccess,
                updateFacilityBulkAutoMatchStatusSuccess
            ),
            map(() => getFacilities())
        )
    );

    showAssignSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                updateCandidateCredentialingSpecialistSuccessfully,
                updateContractCredentialingSpecialistSuccessfully
            ),
            map(() => showToasterMessage({ message: 'Successfully assigned Credentialing Specialist.' }))
        )
    );

    showAssignLiaisonSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                updateLiaisonQmAssociationAssignmentByAssociationIdSuccessfully,
                updateLiaisonFacilityAssignmentByFacilityIdSuccessfully,
                updateContractComplianceLiaisonSuccessfully
            ),
            map(() => showToasterMessage({ message: 'Successfully assigned Compliance Liaison.' }))
        )
    );

    showAssignLiaisonFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                updateLiaisonQmAssociationAssignmentByAssociationIdFailure,
                updateLiaisonFacilityAssignmentByFacilityIdFailure,
                updateContractComplianceLiaisonFailure
            ),
            map(() => showToasterMessage({ message: 'Failed to assign Compliance Liaison.' }))
        )
    );

    showAssignFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateCandidateCredentialingSpecialistFailure, updateContractCredentialingSpecialistFailure),
            map(() => showToasterMessage({ message: 'Failed to assign Credentialing Specialist.' }))
        )
    );

    showFacilityAssignSuccessMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateFacilityAssignmentByFacilityIdSuccessfully),
            map(() => showToasterMessage({ message: 'Successfully assigned Credentialing Specialist for facility.' }))
        )
    );

    showFacilityAssignFailureMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateFacilityAssignmentByFacilityIdFailure),
            map(() => showToasterMessage({ message: 'Failed to assign Credentialing Specialist for facility.' }))
        )
    );

    constructor(
        private readonly _actions$: Actions,
        private readonly _contractAssignmentService: ContractAssignmentService
    ) {}
}
